import { PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT, PARA_SUPPORT_ROLES, type ParaSupportRole } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Para support: the filters live in the URL (`/para-support?q&noc&sport&role&page&size`),
 * like every Participants list — a bad value is dropped, never an error page.
 * The games is the sidebar's current games (a para games, or the page says so).
 */
export const paraSupportSearch = z.object({
	/** The athlete's or the support person's name. */
	q: textParam,
	noc: idParam,
	sport: idParam,
	role: z.enum(PARA_SUPPORT_ROLES).optional().catch(undefined),
	page: pageParam,
	size: sizeParam,
});
export type ParaSupportSearch = z.infer<typeof paraSupportSearch>;

export interface ParaSupportDraft extends Record<string, unknown> {
	q: string;
	noc: string;
	sport: string;
	role: ParaSupportRole | "";
}

export const EMPTY_PARA_SUPPORT_DRAFT: ParaSupportDraft = { q: "", noc: "", sport: "", role: "" };

export const paraSupportDraftFrom = (s: ParaSupportSearch): ParaSupportDraft => ({
	q: s.q ?? "",
	noc: s.noc ?? "",
	sport: s.sport ?? "",
	role: s.role ?? "",
});

/** Draft → URL: empties dropped, the page starts over, the page size stays. */
export const paraSupportSearchFrom = (d: ParaSupportDraft, keep: Pick<ParaSupportSearch, "size"> = {}): ParaSupportSearch => ({
	q: cleanText(d.q),
	noc: d.noc || undefined,
	sport: d.sport || undefined,
	role: d.role || undefined,
	size: keep.size,
});

export const paraSupportFilterCount = (s: ParaSupportSearch): number => [s.q, s.noc, s.sport, s.role].filter((v) => v !== undefined).length;

export const paraSupportPaging = (s: ParaSupportSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? PARA_SUPPORT_LINKS_PAGE_SIZE_DEFAULT });

/** The `GET /para-support-links` query — snake_case, as the wire wants it. */
export interface ParaSupportParams {
	games_id: string;
	search?: string;
	responsible_organization_id?: string;
	sport_id?: string;
	role?: ParaSupportRole;
	page: number;
	page_size: number;
}

export function paraSupportParams(gamesId: string, s: ParaSupportSearch): ParaSupportParams {
	const { page, pageSize } = paraSupportPaging(s);
	return { games_id: gamesId, search: s.q, responsible_organization_id: s.noc, sport_id: s.sport, role: s.role, page, page_size: pageSize };
}
